import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../api'
import { LoadingRows, ErrorState, Empty } from '../components/States'
import { useToast } from '../components/Toasts'
import { PageNumbers } from '../components/PageNumbers'
import { useAsync, useDebounced } from '../lib/useAsync'
import { money } from '../lib/money'
import { pctLabel } from '../lib/bands'
import { plural } from '../lib/format'
import { saveCsv } from '../lib/download'
import type { Pct, RangeRow, PublishedRange, PublishedSet, SalaryRanges as RangesView } from '../types'
import { spreadChoices } from '../lib/spreads'

/**
 * Salary ranges: what the company's ranges are, and what the market suggests
 * they become.
 *
 * **Two tabs, because the screen answers two questions.** One table carrying
 * both meant every row held its figures, a spread control and sometimes a
 * decision nobody had asked to make. Somebody reading their ranges had to read
 * past decisions they did not come for, and somebody reviewing changes had to
 * find them among roles that had none.
 *
 * **Nothing on Current ranges asks to be decided.** That is the rule that
 * keeps it readable, and it is the one to protect when a field is added.
 *
 * **Each row draws its range over the one last published.** That is the only
 * comparison this product can honestly make: the prototype compared against
 * the job description's own pay fields, which 29 of the demo company's 40
 * roles do not have. The figures are written above the bar, so the range is
 * readable without reading the drawing.
 *
 * **The percentile and the spread sit together**, because they answer one
 * question: where the range sits and how wide it is. Neither asks for
 * approval. Only the market's figure waits for a person.
 *
 * **There is no Decline and no Dismiss.** Leaving a row alone is how you
 * decline and the range does not move. A control that cleared a row without
 * moving the range would leave a range drifting from the market with nothing
 * on screen saying so.
 */
export function SalaryRanges() {
  const toast = useToast()
  const navigate = useNavigate()
  const [tab, setTab] = useState<'ranges' | 'changes'>('ranges')
  const [nonce, setNonce] = useState(0)
  const [busy, setBusy] = useState<string | null>(null)

  /*
   * **The page, the search and the family decide the request, so they live
   * here.** They used to sit inside the table and filter the rows in hand,
   * which worked while the whole library came back at once. Against one
   * page of twenty, filtering what is in hand would mean "search this
   * page": somebody types a title, gets nothing, and concludes the role is
   * not there when it is on page four.
   */
  const [page, setPage] = useState(0)
  const [query, setQuery] = useState('')
  const [family, setFamily] = useState('')
  const asked = useDebounced(query, 250)

  // Back to the first page whenever what is being asked for changes, because
  // page four of one search is not page four of the next.
  useEffect(() => { setPage(0) }, [asked, family])

  const state = useAsync(
    () => api.salaryRanges({ page, q: asked, family }),
    [nonce, page, asked, family],
  )

  /*
   * The changes tab asks the same route for the waiting rows, with its own
   * page.
   *
   * It used to pick them out of the rows in hand, which was the whole
   * library. Against one page of twenty it would show whichever happened to
   * be on that page, and somebody would accept four changes believing that
   * was all of them.
   */
  const [changesPage, setChangesPage] = useState(0)
  const changes = useAsync(
    () => api.salaryRanges({ page: changesPage, waiting: true }),
    [nonce, changesPage],
  )
  const waiting = changes.data?.items ?? []
  const set = useAsync(() => api.publishedRanges(), [nonce])
  const reload = () => setNonce((n) => n + 1)

  const fail = (err: unknown) =>
    toast.fail(err instanceof Error ? err.message : 'That did not save.')

  /** The company's own number. No approval, and every row moves. */
  const changeCompanySpread = async (spread: number) => {
    setBusy('company')
    try {
      await api.setCompanySpread(spread)
      reload()
    } catch (err) { fail(err) } finally { setBusy(null) }
  }

  /**
   * Where one role's range sits in the market.
   *
   * No approval, exactly like the spread. It is this company's own choice
   * about where to pay rather than the market's figure moving under them.
   */
  const changePercentile = async (jdId: string, value: string) => {
    setBusy(jdId)
    try {
      await api.setPercentile(jdId, value as Pct)
      reload()
    } catch (err) { fail(err) } finally { setBusy(null) }
  }

  /** One role's own. "Company" takes the override away rather than copying one in. */
  const changeRoleSpread = async (jdId: string, value: string) => {
    setBusy(jdId)
    try {
      if (value === 'inherit') await api.clearRoleSpread(jdId)
      else await api.setRoleSpread(jdId, Number(value))
      reload()
    } catch (err) { fail(err) } finally { setBusy(null) }
  }

  const acceptOne = async (row: RangeRow) => {
    setBusy(row.jdId)
    try {
      await api.acceptRange(row.jdId)
      toast.ok(`${row.title} now uses the market figure.`)
      reload()
    } catch (err) { fail(err) } finally { setBusy(null) }
  }

  /**
   * Fixes the ranges as they stand as the company's published set.
   *
   * It publishes what is there, not what is waiting. A median nobody has
   * accepted is not published and neither is the range it would become, so
   * publishing can never accept things in bulk by accident.
   */
  const publish = async () => {
    setBusy('publish')
    try {
      const out = await api.publishRanges()
      toast.ok(
        out.changed === 0 && out.added === 0
          ? `Published ${plural(out.count, 'range')}. Nothing changed since last time.`
          : `Published ${plural(out.count, 'range')}. ${out.added} new, ${out.changed} changed.`,
      )
      reload()
    } catch (err) { fail(err) } finally { setBusy(null) }
  }

  /*
   * The files, built here from what was published.
   *
   * No route behind them: the browser already has the rows, so there is
   * nothing to leak that it was not told, and nothing is written into a log
   * group. Both builders are fetched when a button is pressed, so nobody who
   * never downloads pays for them.
   */
  const download = async (rows: PublishedRange[], at: string) => {
    setBusy('download')
    try {
      const [{ renderPdf }, file] = await Promise.all([import('../lib/pdf'), import('../lib/ranges-file')])
      await renderPdf(file.rangesBlocks(rows, at), file.rangesFileName(at, 'pdf'), file.RANGES_PDF)
      toast.ok(`Downloaded ${plural(rows.length, 'range')}.`)
    } catch (err) { fail(err) } finally { setBusy(null) }
  }

  const downloadCsv = async (rows: PublishedRange[], at: string) => {
    try {
      const { rangesCsv, rangesFileName } = await import('../lib/ranges-file')
      saveCsv(rangesFileName(at, 'csv'), rangesCsv(rows, at))
      toast.ok(`Downloaded ${plural(rows.length, 'range')} as a spreadsheet.`)
    } catch (err) { fail(err) }
  }

  const acceptEvery = async () => {
    setBusy('all')
    try {
      const done = await api.acceptAllRanges()
      toast.ok(`${plural(done.accepted, 'range')} updated.`)
      reload()
    } catch (err) { fail(err) } finally { setBusy(null) }
  }

  /*
   * The same room under the top bar as Job descriptions and the role screen,
   * on every state, so the heading does not jump when the rows arrive.
   */
  if (state.loading) return <div className="screen ranges"><LoadingRows /></div>
  if (state.error) return <div className="screen ranges"><ErrorState message={state.error} /></div>
  if (!state.data) return null

  const { hasSurvey, spread, counts, items, total } = state.data
  const published = set.data ?? null

  /*
   * A table that cannot be filled is worse than no tab at all. The same thing
   * Benchmarking says, for the same reason: a range is made from a benchmark,
   * and a benchmark is made from a survey.
   */
  if (!hasSurvey) {
    return (
      <div className="screen ranges">
        <header className="screen__head">
          <h1>Salary ranges</h1>
        </header>
        <Empty
          title="There is no market data here to build a range from"
          action={{ label: 'Add a survey', onClick: () => navigate('/market-data/add') }}
        >
          <p>
            A range is the market median for a role, widened both ways. Add a salary
            survey, benchmark your roles against it, and every role that matches will
            have a range here.
          </p>
        </Empty>
      </div>
    )
  }

  return (
    <div className="screen ranges">
      <header className="screen__head">
        <h1>Salary ranges</h1>
        <p className="subline">
          {plural(counts.banded, 'role')} of {total} {counts.banded === 1 ? 'has' : 'have'} a range,
          built from the survey figure each one was benchmarked to, at the percentile it sits on.
        </p>
      </header>

      <div className="tabs" role="tablist" aria-label="Salary ranges">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'ranges'}
          className="tab"
          onClick={() => setTab('ranges')}
        >
          Current ranges
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'changes'}
          className="tab"
          onClick={() => setTab('changes')}
        >
          Suggested changes
          {/* The count, so opening the module shows there is something to look at. */}
          {waiting.length > 0 && <span className="tab__badge">{waiting.length}</span>}
        </button>
      </div>

      {tab === 'ranges'
        ? (
          <CurrentRanges
            items={items}
            view={state.data}
            query={query}
            family={family}
            busyPage={state.loading}
            onQuery={setQuery}
            onFamily={setFamily}
            onPage={setPage}
            spread={spread}
            busy={busy}
            published={published}
            onCompanySpread={changeCompanySpread}
            onRoleSpread={changeRoleSpread}
            onPercentile={changePercentile}
            onPublish={publish}
            onDownload={(rows, at) => void download(rows, at)}
            onDownloadCsv={(rows, at) => void downloadCsv(rows, at)}
          />
        )
        : (
          <SuggestedChanges
            waiting={waiting}
            view={changes.data}
            busyPage={changes.loading}
            onPage={setChangesPage}
            busy={busy}
            onAccept={acceptOne}
            onAcceptAll={acceptEvery}
          />
        )}
    </div>
  )
}

/* ---------- tab one: what the ranges are ---------- */

function CurrentRanges({
  items, spread, busy, published, view, query, family, busyPage,
  onQuery, onFamily, onPage,
  onCompanySpread, onRoleSpread, onPercentile, onPublish, onDownload, onDownloadCsv,
}: {
  items: RangeRow[]
  spread: number
  busy: string | null
  published: PublishedSet | null
  view: RangesView | null
  query: string
  family: string
  busyPage: boolean
  onQuery: (q: string) => void
  onFamily: (f: string) => void
  onPage: (n: number) => void
  onCompanySpread: (spread: number) => void
  onRoleSpread: (jdId: string, value: string) => void
  onPercentile: (jdId: string, value: string) => void
  onPublish: () => void
  onDownload: (rows: PublishedRange[], at: string) => void
  onDownloadCsv: (rows: PublishedRange[], at: string) => void
}) {
  const overrides = items.filter((r) => r.overridden).length
  const everPublished = Boolean(published?.publishedAt)

  /*
   * Every family in the library, not only the ones on this page.
   *
   * This was built from the rows in hand, which was every role while the
   * whole library came back at once. On one page of twenty it would offer
   * whichever families happened to be on that page and lose the rest, so
   * choosing most of them would be impossible.
   */
  const families = view?.families ?? []

  // Already searched, already ordered, already one page.
  const shown = items

  return (
    <>
      <div className="actions ranges__bar">
        {/* Not "Company range spread". The sentence beside it already says
            how many roles have their own, which is what makes this one the
            company's, and the row control is a range spread too. */}
        <label htmlFor="company-spread" className="ranges__label">Range spread</label>
        <select
          id="company-spread"
          className="select"
          value={spread}
          disabled={busy === 'company'}
          onChange={(e) => onCompanySpread(Number(e.target.value))}
        >
          {spreadChoices(spread).map((p) => <option key={p} value={p}>± {p}%</option>)}
        </select>
        <span className="subline">
          {overrides === 0
            ? 'Every role uses it.'
            : `${plural(overrides, 'role')} ${overrides === 1 ? 'has' : 'have'} their own.`}
        </span>

        <span className="ranges__push" />

        {/*
          Nothing is downloadable until something is published. A range on
          screen is a working figure: somebody is still setting the spread and
          still deciding which figures to take, and handing that out as a file
          would put a number nobody agreed on into an offer letter with no
          record to check it against.
        */}
        {everPublished && (
          <button
            type="button"
            className="btn"
            disabled={busy !== null}
            onClick={() => onDownload(published!.items, published!.publishedAt!)}
          >
            &darr; Download published set
          </button>
        )}
        {/* The same set as a spreadsheet, for somebody who wants to sort it. */}
        {everPublished && (
          <button
            type="button"
            className="btn btn--sm btn--quiet"
            disabled={busy !== null}
            onClick={() => onDownloadCsv(published!.items, published!.publishedAt!)}
          >
            Spreadsheet
          </button>
        )}
        <button
          type="button"
          className="btn btn--primary"
          disabled={busy !== null}
          onClick={onPublish}
        >
          {everPublished ? 'Publish again' : 'Publish'}
        </button>
      </div>

      <p className="subline">
        {everPublished
          ? `Published on ${published!.publishedAt!.slice(0, 10)} by ${published!.publishedBy}, `
            + `${plural(published!.items.length, 'range')}. `
            + 'That set stays as it was published until you publish again.'
          : 'Nothing has been published yet, so there is nothing to download. '
            + 'These are working figures until you publish them.'}
      </p>

      {/*
        The search, the filter and the count.

        A company with four hundred roles cannot scroll to the one it came
        for, and the count has to follow the filter or it is answering a
        question nobody asked.
      */}
      <div className="actions ranges__find">
        <label className="field field--search">
          <span className="sr-only">Search roles</span>
          <input
            type="search"
            value={query}
            placeholder="Search job title, level, location"
            onChange={(e) => onQuery(e.target.value)}
          />
        </label>
        <select
          className="select"
          value={family}
          aria-label="Job family"
          onChange={(e) => onFamily(e.target.value)}
        >
          <option value="">All families</option>
          {families.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        {/*
          Where the page sits is said in the pager at the foot, beside the
          numbers, the way Benchmarking and Market data say it. Saying it
          here as well would be the same sentence twice on one screen.
        */}
        {view && view.matched < view.total && (
          <span className="subline">
            {plural(view.matched, 'role')} of {view.total} match this filter
          </span>
        )}
      </div>

      {/*
        The table fits the room it is given (FR-SB53). With Iconic and its
        chat list open the page is about 540 pixels wide, and the range is
        what the screen is for, so the other columns give way first: data
        quality, then level and location, which move under the title.
      */}
      <div className="table-wrap ranges__table">
        <table className="table--figures">
          <thead>
            <tr>
              <th scope="col">Job title</th>
              <th scope="col" className="ranges__col--level">Level</th>
              <th scope="col" className="ranges__col--loc">Location</th>
              <th scope="col" className="ranges__col--quality">Data quality</th>
              <th scope="col">Target percentile &middot; Range spread</th>
              <th scope="col">Range</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((row) => (
              <tr key={row.jdId}>
                <td>
                  {/*
                    The title is the way in, rather than a Details link at the
                    far end of the row.

                    Somebody looking for a role reads the title, so that is
                    where their pointer already is. A link six columns away
                    means crossing the whole row to open the thing they were
                    looking at, and on a narrow screen it is off the side.
                  */}
                  <Link to={`/salary-ranges/${row.jdId}`} className="rowlink">{row.title}</Link>
                  {/* Shown only once their own columns have given way. */}
                  <p className="subline ranges__meta">{row.level} · {row.location || 'no location'}</p>
                  {row.state === 'no-match' && (
                    <p className="subline">The survey does not cover this role</p>
                  )}
                  {row.state === 'never-run' && (
                    <p className="subline">Never benchmarked</p>
                  )}
                  {/*
                    The role was edited, not the market. It needs benchmarking
                    again rather than a decision, so it stays a quiet line here
                    and is never a suggested change.
                  */}
                  {row.outOfDate && (
                    <p className="subline subline--warn">Edited since it was benchmarked</p>
                  )}
                </td>
                <td className="ranges__col--level">{row.level}</td>
                <td className="ranges__col--loc">{row.location || '—'}</td>
                <td className="ranges__col--quality">
                  {/*
                    "none" is what a benchmark that matched nothing carries,
                    and it is not a quality. Showing it as a pill puts a word
                    where there is no measurement.
                  */}
                  {row.confidence && row.confidence.toLowerCase() !== 'none'
                    ? (
                      <span className={`pill pill--${quality(row.confidence)}`}>
                        {sentence(row.confidence)}
                      </span>
                    )
                    : '—'}
                </td>
                <td>
                  {row.midpoint !== null && (
                    <div className="dials">
                      <PercentilePicker row={row} busy={busy === row.jdId} onPick={onPercentile} />
                      <SpreadPicker
                        row={row}
                        company={spread}
                        busy={busy === row.jdId}
                        onPick={onRoleSpread}
                      />
                    </div>
                  )}
                </td>
                <td>
                  {row.midpoint === null
                    ? <span className="subline">No range</span>
                    : <RowRange row={row} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/*
        Nothing found, told apart from nothing in the library.

        `matched` counts what the search left across the whole library, so
        an empty page is an empty search rather than the end of the rows.
      */}
      {shown.length === 0 && view !== null && view.total > 0 && (
        <Empty title="No roles match this filter">
          <p>Clear the search or pick another job family.</p>
        </Empty>
      )}

      {/*
        The same numbered pager as Market data and Benchmarking.

        `open` is false, because every role is already in hand when a page is
        chosen, so the number of pages is known exactly and no number offered
        leads nowhere.
      */}
      {/*
        The same footer as Benchmarking and Market data: where you are on the
        left, the numbers on the right. It was added bare, so the numbers sat
        under the left edge of the table and read as part of it rather than
        as a control.

        Nothing where there is one page. A pager over nine rows is furniture.
      */}
      {view && view.pages > 1 && (
        <nav className="pager" aria-label="Salary range pages">
          <p className="subline">
            Page {view.page + 1} of {view.pages} · showing {view.page * 20 + 1} to{' '}
            {view.page * 20 + shown.length} of {plural(view.matched, 'role')}
          </p>
          <PageNumbers
            at={view.page}
            pages={view.pages}
            open={false}
            busy={busyPage}
            onGo={onPage}
          />
        </nav>
      )}
    </>
  )
}

/**
 * Where one role's range sits in the market.
 *
 * **It offers that role's own percentiles and no others.** A real survey fills
 * in some of the four and leaves the rest empty, and which ones differ by
 * role: on the demo company's 35 banded roles, 31 have a 25th and not one has
 * a 90th. A fixed list would offer every role at least one figure that does
 * not exist.
 *
 * **A role with one percentile gets no control.** A choice of one is not a
 * choice, and a dropdown that cannot be changed reads as a thing that is
 * broken.
 */
function PercentilePicker({ row, busy, onPick }: {
  row: RangeRow
  busy: boolean
  onPick: (jdId: string, value: string) => void
}) {
  if (row.percentiles.length < 2) {
    // Sized like the control it stands in for, so the spread beside it lines
    // up down the column whether or not a role was offered a choice.
    return <span className="dials__fixed">{pctLabel(row.percentile)}</span>
  }
  return (
    <select
      className="select select--sm select--pct"
      value={row.percentile}
      disabled={busy}
      aria-label={`Target percentile for ${row.title}`}
      onChange={(e) => onPick(row.jdId, e.target.value)}
    >
      {row.percentiles.map((p) => <option key={p} value={p}>{pctLabel(p)}</option>)}
    </select>
  )
}

/**
 * How wide one role's range is.
 *
 * **Every option is a percentage.** The first used to read "Company", which
 * named where the number came from instead of saying what it was, so a column
 * of them told somebody nothing about how wide any of their ranges were.
 *
 * The company's own number is that first option, so choosing it puts the role
 * back on the shared one and the role follows the company from then on. It is
 * offered once: an explicit option for the same percentage would read
 * identically and do something subtly different.
 *
 * A role on its own shows the control marked, which is how the two are told
 * apart now that they read the same way.
 */
function SpreadPicker({ row, company, busy, onPick }: {
  row: RangeRow
  company: number
  busy: boolean
  onPick: (jdId: string, value: string) => void
}) {
  return (
    <select
      className={`select select--sm select--spread${row.overridden ? ' select--own' : ''}`}
      value={row.overridden ? String(row.spread) : 'inherit'}
      disabled={busy}
      aria-label={`Range spread for ${row.title}`}
      onChange={(e) => onPick(row.jdId, e.target.value)}
    >
      <option value="inherit">± {company}%</option>
      {spreadChoices(row.overridden ? row.spread : undefined).filter((p) => p !== company).map((p) => (
        <option key={p} value={p}>± {p}%</option>
      ))}
    </select>
  )
}

/**
 * One row's range, drawn, with its figures written above it.
 *
 * **Scaled to its own figures**, never to a scale shared down the table.
 * Roles here are priced in different currencies and different periods, so one
 * axis would draw an hourly rate against a yearly salary.
 *
 * **The figures are text, not only a shape.** The non-functional requirements
 * have always said a chart needs a text equivalent, with a pay band as the
 * example, and the drawing itself carries one accessible name rather than
 * announcing itself as a row of empty boxes.
 */
function RowRange({ row }: { row: RangeRow }) {
  const min = row.minimum!
  const mid = row.midpoint!
  const max = row.maximum!

  // Room at each end, so the edge of the bar is not the edge of the cell.
  const pad = (max - min) * 0.12 || Math.max(min * 0.05, 1)
  const from = min - pad
  const span = (max + pad) - from
  const x = (v: number) => ((v - from) / span) * 100

  const words = `${money(min, row.currency)} to ${money(max, row.currency)}, `
    + `midpoint ${money(mid, row.currency)} at the ${pctLabel(row.percentile)} percentile`

  return (
    <div className="rowrange">
      {/*
        The three figures as a row, not pinned to the points they mark. The
        bar below already says where each figure sits, and pinned they
        collide as soon as a range is narrow.
      */}
      <div className="rowrange__figures">
        <span className="rowrange__lbl">{money(min, row.currency)}</span>
        <span className="rowrange__lbl rowrange__lbl--mid">{money(mid, row.currency)}</span>
        <span className="rowrange__lbl">{money(max, row.currency)}</span>
      </div>

      <div className="rowrange__axis" role="img" aria-label={words}>
        <span
          className="rowrange__now"
          style={{ left: `${x(min)}%`, width: `${x(max) - x(min)}%` }}
        />
        <span className="rowrange__mid" style={{ left: `${x(mid)}%` }} />
      </div>
    </div>
  )
}

/* ---------- tab two: what the market suggests ---------- */

function SuggestedChanges({ waiting, view, busyPage, onPage, busy, onAccept, onAcceptAll }: {
  waiting: RangeRow[]
  view: RangesView | null
  busyPage: boolean
  onPage: (n: number) => void
  busy: string | null
  onAccept: (row: RangeRow) => void
  onAcceptAll: () => void
}) {
  if (waiting.length === 0) {
    return (
      <Empty title="Nothing to review">
        <p>
          Every range is built on the market figure you last accepted. If benchmarking
          finds a different one, it will appear here.
        </p>
      </Empty>
    )
  }

  return (
    <>
      <div className="actions ranges__bar ranges__bar--apart">
        <p className="subline">
          Benchmarking found a different market median for {plural(waiting.length, 'role')}.
          Nothing has changed yet: each range stays as it is until you accept it here.
        </p>
        <button
          type="button"
          className="btn btn--primary"
          disabled={busy !== null}
          onClick={() => onAcceptAll()}
        >
          Accept all {waiting.length}
        </button>
      </div>

      <div className="table-wrap">
        <table className="table--figures">
          <thead>
            <tr>
              <th scope="col">Job title</th>
              <th scope="col" className="num">Current midpoint</th>
              <th scope="col" className="num">Suggested midpoint</th>
              <th scope="col" className="num">Change</th>
              <th scope="col"></th>
            </tr>
          </thead>
          <tbody>
            {waiting.map((row) => {
              const move = row.waiting!
              const pct = ((move.to - move.from) / move.from) * 100
              // The ranges either side, so the comparison is between two
              // ranges rather than between two midpoints with ranges implied.
              const width = row.spread / 100
              const now = { lo: move.from * (1 - width), hi: move.from * (1 + width) }
              const next = { lo: move.to * (1 - width), hi: move.to * (1 + width) }
              return (
                <tr key={row.jdId}>
                  <td>
                    <strong>{row.title}</strong>
                    <p className="subline">{row.level} · {row.location || 'no location'} · ± {row.spread}%</p>
                  </td>
                  <td className="num">
                    {money(move.from, row.currency)}
                    <p className="subline num">{money(now.lo, row.currency)} – {money(now.hi, row.currency)}</p>
                  </td>
                  <td className="num num--strong">
                    {money(move.to, row.currency)}
                    <p className="subline num">{money(next.lo, row.currency)} – {money(next.hi, row.currency)}</p>
                  </td>
                  <td className={`num ${pct >= 0 ? 'num--up' : 'num--down'}`}>
                    {pct >= 0 ? '+' : '−'}{Math.abs(pct).toFixed(1)}%
                  </td>
                  <td className="num">
                    <button
                      type="button"
                      className="btn btn--primary btn--sm"
                      disabled={busy !== null}
                      onClick={() => onAccept(row)}
                    >
                      Accept
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/*
        The same pager as the ranges tab, because this is the same list with
        one filter on it. Accepting every waiting change is its own route and
        covers the library whatever page is on screen.
      */}
      {view && view.pages > 1 && (
        <nav className="pager" aria-label="Waiting change pages">
          <p className="subline">
            Page {view.page + 1} of {view.pages} · showing {view.page * 20 + 1} to{' '}
            {view.page * 20 + waiting.length} of {plural(view.matched, 'change')}
          </p>
          <PageNumbers
            at={view.page}
            pages={view.pages}
            open={false}
            busy={busyPage}
            onGo={onPage}
          />
        </nav>
      )}

      <p className="subline">
        Leaving one alone keeps the range you already have. There is nothing to decline.
      </p>
    </>
  )
}

/** How good the data behind a range is, as the pill the rest of the product uses. */
function quality(confidence: string): string {
  const word = confidence.toLowerCase()
  if (word.startsWith('high')) return 'published'
  if (word.startsWith('medium')) return 'review'
  return 'draft'
}

/** The provider's own word, with a capital, because it starts a label. */
const sentence = (word: string) => word.charAt(0).toUpperCase() + word.slice(1)
